import TreeContainer from './Base';
import TreeIterator from './Base/TreeIterator';
import $checkWithinAccessParams from "../../utils/checkParams.macro";
import { throwIteratorAccessError } from "../../utils/throwError";
class OrderedMapIterator extends TreeIterator {
    constructor(node, header, container, iteratorType) {
        super(node, header, iteratorType);
        this.container = container;
    }
    get pointer() {
        if (this._node === this._header) {
            throwIteratorAccessError();
        }
        const self = this;
        return new Proxy([], {
            get(target, prop) {
                if (prop === '0')
                    return self._node._key;
                else if (prop === '1')
                    return self._node._value;
                target[0] = self._node._key;
                target[1] = self._node._value;
                return target[prop];
            },
            set(_, prop, newValue) {
                if (prop !== '1') {
                    throw new TypeError('prop must be 1');
                }
                self._node._value = newValue;
                return true;
            }
        });
    }
    copy() {
        return new OrderedMapIterator(this._node, this._header, this.container, this.iteratorType);
    }
}
class OrderedMap extends TreeContainer {
    /**
     * @param container - The initialization container.
     * @param cmp - The compare function.
     * @param enableIndex - Whether to enable iterator indexing function.
     * @example
     * new OrderedMap();
     * new OrderedMap([[0, 1], [2, 1]]);
     * new OrderedMap([[0, 1], [2, 1]], (x, y) => x - y);
     * new OrderedMap([[0, 1], [2, 1]], (x, y) => x - y, true);
     */
    constructor(container = [], cmp, enableIndex) {
        super(cmp, enableIndex);
        const self = this;
        container.forEach(function (el) {
            self.setElement(el[0], el[1]);
        });
    }
    begin() {
        return new OrderedMapIterator(this._header._left || this._header, this._header, this);
    }
    end() {
        return new OrderedMapIterator(this._header, this._header, this);
    }
    rBegin() {
        return new OrderedMapIterator(this._header._right || this._header, this._header, this, 1 /* IteratorType.REVERSE */);
    }
    rEnd() {
        return new OrderedMapIterator(this._header, this._header, this, 1 /* IteratorType.REVERSE */);
    }
    front() {
        if (this._length === 0)
            return;
        const minNode = this._header._left;
        return [minNode._key, minNode._value];
    }
    back() {
        if (this._length === 0)
            return;
        const maxNode = this._header._right;
        return [maxNode._key, maxNode._value];
    }
    lowerBound(key) {
        const resNode = this._lowerBound(this._root, key);
        return new OrderedMapIterator(resNode, this._header, this);
    }
    upperBound(key) {
        const resNode = this._upperBound(this._root, key);
        return new OrderedMapIterator(resNode, this._header, this);
    }
    reverseLowerBound(key) {
        const resNode = this._reverseLowerBound(this._root, key);
        return new OrderedMapIterator(resNode, this._header, this);
    }
    reverseUpperBound(key) {
        const resNode = this._reverseUpperBound(this._root, key);
        return new OrderedMapIterator(resNode, this._header, this);
    }
    forEach(callback) {
        this._inOrderTraversal(function (node, index, map) {
            callback([node._key, node._value], index, map);
        });
    }
    /**
     * @description Insert a key-value pair or set value by the given key.
     * @param key - The key want to insert.
     * @param value - The value want to set.
     * @param hint - You can give an iterator hint to improve insertion efficiency.
     * @return The size of container after setting.
     * @example
     * const mp = new OrderedMap([[2, 0], [4, 0], [5, 0]]);
     * const iter = mp.begin();
     * mp.setElement(1, 0);
     * mp.setElement(3, 0, iter);  // give a hint will be faster.
     */
    setElement(key, value, hint) {
        return this._set(key, value, hint);
    }
    getElementByPos(pos) {
        if (pos < 0 || pos > this._length - 1) {
            throw new RangeError();
        }
        const node = this._inOrderTraversal(pos);
        return [node._key, node._value];
    }
    find(key) {
        const curNode = this._getTreeNodeByKey(this._root, key);
        return new OrderedMapIterator(curNode, this._header, this);
    }
    /**
     * @description Get the value of the element of the specified key.
     * @param key - The specified key you want to get.
     * @example
     * const val = container.getElementByKey(1);
     */
    getElementByKey(key) {
        const curNode = this._getTreeNodeByKey(this._root, key);
        return curNode._value;
    }
    union(other) {
        const self = this;
        other.forEach(function (el) {
            self.setElement(el[0], el[1]);
        });
        return this._length;
    }
    *[Symbol.iterator]() {
        const length = this._length;
        const nodeList = this._inOrderTraversal();
        for (let i = 0; i < length; ++i) {
            const node = nodeList[i];
            yield [node._key, node._value];
        }
    }
}
export default OrderedMap;
